/*
 * PROJECT-SUMMARY.JSX - Project Summary Dashboard Component
 * 
 * SUMMARY:
 * Displays a comprehensive project overview dashboard with project details, budget summary,
 * and financial information. Includes progress bars for material, resource, contractor,
 * and time allocations with visual indicators for budget tracking.
 * 
 * MAJOR COMPONENTS:
 * - MultiSegmentProgressBar - Custom progress bar with 100% allowance marker
 * - ProjectSummary - Main component displaying project overview cards
 * 
 * FEATURES:
 * - Project details display (name, address, customer, contact, manager)
 * - Budget summary with progress bars for material, resource, contractor spending
 * - Time tracking with allocated vs spent hours
 * - Financial summary (contract, variations, claims, retention, balance)
 * - Responsive grid layout for different screen sizes
 * - Conditional rendering for tender vs project modes
 * 
 * USE CASES:
 * - Project dashboard overview
 * - Budget monitoring and tracking
 * - Financial reporting
 * - Progress visualization
 * - Stakeholder communication
 */

import React from "react";
import { Grid } from "@material-ui/core";
import "./scss/project-management.scss";
import { Box, Stack, Typography, Select, MenuItem, FormControl, CircularProgress } from "@mui/material";
import { numberFormat, toUSD } from "../../util/currency-formatter";
import { ProgressBar } from "react-bootstrap";
import { timeToHours } from "../../util/common";
import { PROJECT_STATUS } from "../../util/enums";

/**
 * MultiSegmentProgressBar Component
 * 
 * @description Renders a custom progress bar with a visual marker at 100% allowance.
 * Shows progress beyond 100% with different color coding and includes a vertical
 * marker line to indicate the 100% threshold.
 * 
 * @param {Object} props - Component props
 * @param {number|string} props.workdone - Current work completed/spent amount
 * @param {number|string} props.allowance - Total allocated allowance/budget
 * @returns {JSX.Element} Progress bar with visual indicators
 * 
 * @example
 * <MultiSegmentProgressBar workdone={75000} allowance={100000} />
 * // Shows 75% progress with green color
 * 
 */
const MultiSegmentProgressBar = ({ workdone, allowance }) => {
  const currentWorkdone = Math.max(0, parseFloat(workdone) || 0);
  const currentAllowance = Math.max(0, parseFloat(allowance) || 0);
  let workdonePercentage = 0;
  if (currentAllowance > 0) workdonePercentage = (currentWorkdone / currentAllowance) * 100;
  else if (currentWorkdone > 0) workdonePercentage = 120; //total progressbar percentage

  const displayFillPercentage = (workdonePercentage / 120) * 100;
  const fillColorClass = workdonePercentage === 0 ? 'btn-bg-brand-2' : workdonePercentage <= 100 ? "btn-bg-brand-3" : "btn-bg-brand-5";
  const markerPositionPercent = (100 / 120) * 100;

  return (
    <div
      className="progress-container rounded-5"
      style={{
        position: 'relative',
        height: '20px',
        width: '100%'
      }}
    >
      <ProgressBar
        now={120}
        className='btn-bg-brand-2'
        style={{
          height: '100%',
          transition: 'width 0.3s ease-in-out'
        }}>
        <ProgressBar
          now={displayFillPercentage}
          className={fillColorClass}
          style={{
            height: '100%',
            transition: 'width 0.3s ease-in-out'
          }}
        />
      </ProgressBar>

      <div
        style={{
          position: 'absolute',
          left: `${markerPositionPercent}%`,
          top: 0,
          bottom: 0,
          width: '2px',
          backgroundColor: '#333333',
          zIndex: 1,
        }}
        title="100% Allowance Mark"
      />
    </div>
  );
};

/**
 * ProjectSummary Component
 * 
 * @description Main project overview dashboard component that displays project details,
 * budget summary with progress bars, and financial information in a responsive grid layout.
 * Conditionally renders different sections based on whether it's a tender or project.
 * 
 * @param {Object} props - Component props containing project data
 * @param {string} props.projectName - Name of the project or tender
 * @param {string} props.projectManagerName - Name of assigned project manager
 * @param {boolean} props.moduleType - True for tender, false for project
 * @param {string} props.customerName - Customer/client name
 * @param {string} props.contactData - Customer contact information
 * @param {number} props.totalVariationAmount - Total variation amount
 * @param {number} props.totalRevisedContractCost - Revised contract cost
 * @param {number} props.totalClaimAmount - Total claimed amount
 * @param {number} props.totalRetentionAmount - Total retention amount
 * @param {number} props.totalContract - Original contract amount
 * @param {string} props.siteAddress - Project site address
 * @param {number} props.materialAllowance - Material budget allowance
 * @param {number} props.resourceAllowance - Resource budget allowance
 * @param {number} props.contractorAllowance - Contractor budget allowance
 * @param {number} props.materialActualSpending - Actual material spending
 * @param {number} props.resourceActualSpending - Actual resource spending
 * @param {number} props.contractorActualSpending - Actual contractor spending
 * @param {string} props.timeSpend - Time spent in HH:MM format
 * @param {number} props.balanceAmount - Remaining balance amount
 * @param {string} props.timeAllocated - Allocated time in HH:MM format
 * @returns {JSX.Element} Project summary dashboard
 * 
 * @example
 * <ProjectSummary
 *   projectName="Office Building Project"
 *   projectManagerName="John Doe"
 *   moduleType={false}
 *   customerName="ABC Corp"
 *   totalContract={500000}
 *   materialAllowance={200000}
 *   materialActualSpending={150000}
 *   // ... other props
 * />
 */
function ProjectSummary(props) {

  const {
    // projectId,
    projectName,
    projectManagerName,
    moduleType,
    customerName,
    contactData,
    totalVariationAmount,
    totalRevisedContractCost,
    totalClaimAmount,
    totalRetentionAmount,
    totalContract,
    siteAddress,
    materialAllowance,
    resourceAllowance,
    contractorAllowance,
    materialActualSpending,
    resourceActualSpending,
    contractorActualSpending,
    timeSpend,
    balanceAmount,
    timeAllocated,
    status,
    onStatusChange,
    statusLoading
  } = props;

  /**
   * Calculates and formats the percentage of work done relative to allowance.
   * 
   * @param {Object} params - Calculation parameters
   * @param {number} params.workdone - Amount of work completed/spent
   * @param {number} params.allowance - Total allocated allowance
   * @returns {string} Formatted percentage string
   * 
   * @example
   * getRemainingPercentage({ workdone: 75000, allowance: 100000 }); // Returns: "75.00%"
   */
  const getRemainingPercentage = ({ workdone, allowance }) => {
    return numberFormat((workdone / allowance) * 100, true) + '%';
  }

  // Convert time strings to hours for progress bar
  const timeSpendHours = timeToHours(timeSpend); // e.g., "21:00" -> 21
  const timeAllocatedHours = timeToHours(timeAllocated); // e.g., "40:30" -> 40.5

  return (
    <div className="projectOverviewContainer" style={{ margin: "0" }}>
      <div className="progress-fin progress-cards" >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={6} lg={5} xl={4}>
            <Stack className="card customer-details">
              <div className="card-header" data-cy="project-details-widget">
                <Typography variant="h6">{`${moduleType ? "Tender" : "Project"} Detail`}</Typography>
              </div>
              <Stack className="card-details">
                <Grid container className="financial-text">
                  <Grid item xs={4} md={5} xl={4}>
                    <Typography variant="body1">{`${moduleType ? "Tender" : "Project"} Name`}</Typography>
                  </Grid>
                  <Grid item xs={1} md={1} xl={1}>
                    <Typography variant="body1">-</Typography>
                  </Grid>
                  <Grid item xs={7} md={6} xl={7}>
                    <Typography variant="body1" className="text-bold">
                      {projectName}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container className="financial-text">
                  <Grid item xs={4} md={5} xl={4}>
                    <Typography variant="body1">Address </Typography>
                  </Grid>
                  <Grid item xs={1} md={1} xl={1}>
                    <Typography variant="body1">-</Typography>
                  </Grid>
                  <Grid item xs={7} md={6} xl={7}>
                    <Typography variant="body1" className="text-bold" data-cy="project-address">
                      {siteAddress}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container className="financial-text">
                  <Grid item xs={4} md={5} xl={4}>
                    <Typography variant="body1">Customer </Typography>
                  </Grid>
                  <Grid item xs={1} md={1} xl={1}>
                    <Typography variant="body1">-</Typography>
                  </Grid>
                  <Grid item xs={7} md={6} xl={7}>
                    <Typography variant="body1" className="text-bold">
                      {customerName}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container className="financial-text">
                  <Grid item xs={4} md={5} xl={4}>
                    <Typography variant="body1">Contact </Typography>
                  </Grid>
                  <Grid item xs={1} md={1} xl={1}>
                    <Typography variant="body1">-</Typography>
                  </Grid>
                  <Grid item xs={7} md={6} xl={7}>
                    <Typography variant="body1" className="text-bold">
                      {contactData}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container className="financial-text">
                  <Grid item xs={4} md={5} xl={4}>
                    <Typography variant="body1">Assigned To </Typography>
                  </Grid>
                  <Grid item xs={1} md={1} xl={1}>
                    <Typography variant="body1">-</Typography>
                  </Grid>
                  <Grid item xs={7} md={6} xl={7}>
                    <Typography variant="body1" className="text-bold">
                      {projectManagerName}
                    </Typography>
                  </Grid>
                </Grid>
                {moduleType && status !== PROJECT_STATUS.ARCHIVED && (
                  <Grid container className="financial-text" alignItems="center">
                    <Grid item xs={4} md={5} xl={4}>
                      <Typography variant="body1">Status</Typography>
                    </Grid>
                    <Grid item xs={1} md={1} xl={1}>
                      <Typography variant="body1">-</Typography>
                    </Grid>
                    <Grid item xs={7} md={6} xl={7}>
                      <Box sx={{ position: 'relative', width: '100%' }}>
                        <FormControl fullWidth variant="standard" size="small">
                          <Select
                            value={status || ''}
                            onChange={(e) => onStatusChange && onStatusChange(e.target.value)}
                            // disabled={statusLoading}
                            IconComponent={statusLoading ? () => <Box sx={{ width: 24 }} /> : undefined}
                            sx={{
                              '& .MuiSelect-select': {
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#333', // Dark text matches card better
                                paddingRight: '24px !important',
                                paddingBottom: '4px !important'
                              },
                              '& .MuiSelect-icon': {
                                color: 'var(--brand-color-1)',
                                right: 0
                              }
                            }}
                          >
                            <MenuItem value="" disabled>
                              Select Status
                            </MenuItem>
                            <MenuItem value={PROJECT_STATUS.COSTED}>Costed</MenuItem>
                            <MenuItem value={PROJECT_STATUS.TENDER_SUBMITTED}>Submitted</MenuItem>
                            <MenuItem value={PROJECT_STATUS.PENDING}>Pending</MenuItem>
                            <MenuItem value={PROJECT_STATUS.LOI}>LOI</MenuItem>
                            <MenuItem value={PROJECT_STATUS.IN_PROGRESS}>In Progress</MenuItem>
                          </Select>
                        </FormControl>
                        {statusLoading && (
                          <Box sx={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            zIndex: 1
                          }}>
                            <CircularProgress size={16} sx={{ color: 'var(--brand-color-1)' }} />
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Stack>
            </Stack>
          </Grid>
          {!moduleType && (
            <>
              <Grid item xs={12} sm={12} md={6} lg={3} xl={4}>
                <Stack className="card">
                  <div className="card-header" data-cy="budget-summary-widget">
                    <Typography variant="h6">Budget Summary</Typography>
                  </div>
                  <Stack className="card-details">
                    <Stack data-cy="material-bar">
                      <Typography variant="body1">Material</Typography>
                      <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"}>
                        <Box sx={{ width: "70%" }}>
                          <MultiSegmentProgressBar
                            workdone={materialActualSpending}
                            allowance={materialAllowance}
                          />
                        </Box>
                        <Box>
                          <Typography variant="body1" data-cy="content">
                            {getRemainingPercentage({ workdone: materialActualSpending, allowance: materialAllowance })}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                    <Stack mt={1.5} data-cy="resource-bar">
                      <Typography variant="body1">Resource</Typography>
                      <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"}>
                        <Box sx={{ width: "70%" }}>
                          <MultiSegmentProgressBar
                            workdone={resourceActualSpending}
                            allowance={resourceAllowance}
                          />
                        </Box>
                        <Box>
                          <Typography variant="body1" data-cy="content">
                            {getRemainingPercentage({ workdone: resourceActualSpending, allowance: resourceAllowance })}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                    <Stack mt={1.5} data-cy="contractor-bar">
                      <Typography variant="body1">Contractor</Typography>
                      <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"}>
                        <Box sx={{ width: "70%" }}>
                          <MultiSegmentProgressBar
                            workdone={contractorActualSpending}
                            allowance={contractorAllowance}
                          />
                        </Box>
                        <Box>
                          <Typography variant="body1" data-cy="content">
                            {getRemainingPercentage({ workdone: contractorActualSpending, allowance: contractorAllowance })}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                    <Stack mt={1.5} data-cy="time-spend-bar">
                      <Typography variant="body1">Time Spend</Typography>
                      <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"}>
                        <Box sx={{ width: "70%", marginBottom: "16px" }}>
                          <MultiSegmentProgressBar
                            workdone={timeSpendHours}
                            allowance={timeAllocatedHours}
                          />
                        </Box>
                        <Box>
                          <Typography variant="body1" className="mb-3" data-cy="content">
                            {timeSpend}{timeSpendHours > 1 ? "Hrs" : "Hr"}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                <Stack className="card">
                  <div className="card-header" data-cy="finance-widget">
                    <Typography variant="h6">Finance</Typography>
                  </div>
                  <Stack className="card-details">
                    <Grid container className="financial-text">
                      <Grid item xs={4} md={5} xl={7}>
                        <Typography variant="body1">Contract </Typography>
                      </Grid>
                      <Grid item xs={1} md={1} xl={1}>
                        <Typography variant="body1">-</Typography>
                      </Grid>
                      <Grid item xs={7} md={6} xl={4}>
                        <Typography variant="body1" className="text-bold text-right" data-cy="contract-amount">
                          {toUSD(totalContract || 0)}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container className="financial-text">
                      <Grid item xs={4} md={5} xl={7}>
                        <Typography variant="body1">Variations </Typography>
                      </Grid>
                      <Grid item xs={1} md={1} xl={1}>
                        <Typography variant="body1">-</Typography>
                      </Grid>
                      <Grid item xs={7} md={6} xl={4}>
                        <Typography variant="body1" className="text-bold text-right" data-cy="variation-amount">
                          {toUSD(totalVariationAmount || 0)}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container className="financial-text">
                      <Grid item xs={4} md={5} xl={7}>
                        <Typography variant="body1">Revised Contract</Typography>
                      </Grid>
                      <Grid item xs={1} md={1} xl={1}>
                        <Typography variant="body1">-</Typography>
                      </Grid>
                      <Grid item xs={7} md={6} xl={4}>
                        <Typography variant="body1" className="text-bold text-right" data-cy="revised-contract-amount">
                          {toUSD(totalRevisedContractCost || 0)}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container className="financial-text">
                      <Grid item xs={4} md={5} xl={7}>
                        <Typography variant="body1">Claimed</Typography>
                      </Grid>
                      <Grid item xs={1} md={1} xl={1}>
                        <Typography variant="body1">-</Typography>
                      </Grid>
                      <Grid item xs={7} md={6} xl={4}>
                        <Typography variant="body1" className="text-bold text-right" data-cy="claimed-amount">
                          {toUSD(totalClaimAmount || 0)}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container className="financial-text">
                      <Grid item xs={4} md={5} xl={7}>
                        <Typography variant="body1">Retention Held</Typography>
                      </Grid>
                      <Grid item xs={1} md={1} xl={1}>
                        <Typography variant="body1">-</Typography>
                      </Grid>
                      <Grid item xs={7} md={6} xl={4}>
                        <Typography variant="body1" className="text-bold text-right" data-cy="retention-amount">
                          {toUSD(totalRetentionAmount || 0)}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container className="financial-text">
                      <Grid item xs={4} md={5} xl={7}>
                        <Typography variant="body1">Balance</Typography>
                      </Grid>
                      <Grid item xs={1} md={1} xl={1}>
                        <Typography variant="body1">-</Typography>
                      </Grid>
                      <Grid item xs={7} md={6} xl={4}>
                        <Typography variant="body1" className="text-bold text-right" data-cy="balance-amount">
                          {toUSD(balanceAmount || 0)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Stack>
                </Stack>
              </Grid>
            </>
          )}
        </Grid>
      </div>
    </div>
  );
}

export default ProjectSummary;